import React, { useState, useEffect } from "react";
import axios from "axios";
import "./ProjectWorkflow.css"; // Ensures your new CSS is applied

export default function ProjectWorkflow({ project, onBack, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  
  // Local state for Warranty Form
  const [warranty, setWarranty] = useState({
    panelWarranty: "",
    inverterName: "",
    inverterWarranty: "",
    startDate: "",
    endDate: ""
  });

  // Load existing warranty data when project loads
  useEffect(() => {
    if (project.warrantyDetails) {
      setWarranty(project.warrantyDetails);
    }
  }, [project]);

  // Document Types Mapping
  const docTypes = [
    { key: "quotation", label: "📄 Feasibility Letter" },
    { key: "agreement", label: "📝 Net Metering Agreement" },
    { key: "layout", label: "📐 Commissioning Certificate" },
    { key: "netMeter", label: "⚡ Project Completion Certificate" },
    { key: "subsidy", label: "📄 Operation & Maintenance Guideline" },
    { key: "commissioning", label: "✅ Panel & Inverter Data" },
  ];

  /* ================= HANDLE STATUS UPDATE ================= */
  const updateStageStatus = async (stageKey, newStatus) => {
    try {
      const res = await axios.patch(`https://meetra-green-energy-backend.onrender.com/api/projects/staff/update-progress/${project._id}`, {
        stage: stageKey,
        value: newStatus
      });
      onUpdate(res.data);
    } catch (err) {
      alert("Failed to update status");
    }
  };

  /* ================= HANDLE FILE UPLOAD ================= */
  const handleFileUpload = async (event, docKey) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("docType", docKey);

    setUploading(true);
    try {
      const res = await axios.post(`https://meetra-green-energy-backend.onrender.com/api/projects/${project._id}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      onUpdate(res.data);
      alert("Document uploaded successfully!");
    } catch (err) {
      alert("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  /* ================= HANDLE WARRANTY START ================= */
  const handleStartWarranty = () => {
    const today = new Date();
    const formattedDate = today.toISOString().split("T")[0]; // YYYY-MM-DD

    // Auto-calculate end date (Default +25 years for panels, can be edited)
    const endDate = new Date();
    endDate.setFullYear(today.getFullYear() + 25);
    const formattedEndDate = endDate.toISOString().split("T")[0];

    setWarranty(prev => ({
      ...prev,
      startDate: formattedDate,
      endDate: formattedEndDate // Optional: Pre-fill end date
    }));
  };

  /* ================= HANDLE WARRANTY SAVE ================= */
  const handleWarrantySave = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.patch(`https://meetra-green-energy-backend.onrender.com/api/projects/${project._id}/warranty`, warranty);
      onUpdate(res.data);
      alert("✅ Warranty details updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to save warranty details.");
    }
  };

  return (
    <div className="project-workflow">
      <button onClick={onBack} className="back-btn">← Back to Projects</button>
      
      <div className="workflow-header">
        <h2>{project.clientName}</h2>
        <div className="project-meta">
          <span className="project-id">ID: {project.clientRef}</span>
          <span className="project-loc">📍 {project.siteLocation}</span>
        </div>
      </div>

      <div className="workflow-grid">
        
        {/* ================= LEFT: WORKFLOW STAGES (UPDATED UI) ================= */}
        <div className="stages-list">
          <h3>🚀 Project Stages</h3>
          
          <div className="timeline-container">
            <div className="timeline-line"></div>
            
            {Object.keys(project.progressFlow || {}).map((key) => (
              <div key={key} className={`timeline-item ${project.progressFlow[key]}`}>
                
                {/* Icon Box */}
                <div className="timeline-icon">
                  {project.progressFlow[key] === "completed" ? "✔" : "●"}
                </div>

                {/* Content */}
                <div className="timeline-content">
                  <span className="timeline-title">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                  
                  <select 
                    value={project.progressFlow[key]} 
                    onChange={(e) => updateStageStatus(key, e.target.value)}
                    className={`status-select ${project.progressFlow[key]}`}
                  >
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT: DOCUMENT UPLOADS */}
        <div className="documents-upload-section">
          <h3>📂 Project Documents</h3>
          <ul className="doc-upload-list">
            {docTypes.map((doc) => {
              const fileUrl = project.documents?.[doc.key];
              return (
                <li key={doc.key} className="doc-item">
                  <span className="doc-label">{doc.label}</span>
                  <div className="doc-actions">
                    {fileUrl ? <a href={`https://meetra-green-energy-backend.onrender.com${fileUrl}`} target="_blank" rel="noreferrer" className="view-link">View</a> : <span className="not-uploaded">Pending</span>}
                    <label className={`upload-btn ${uploading ? "disabled" : ""}`}>
                      {fileUrl ? "Re-upload" : "Upload"}
                      <input type="file" hidden onChange={(e) => handleFileUpload(e, doc.key)} disabled={uploading} />
                    </label>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* ================= ✅ NEW WARRANTY SECTION ================= */}
      <div className="warranty-section" style={{ marginTop: "30px", background: "white", padding: "20px", borderRadius: "10px", boxShadow: "0 2px 5px rgba(0,0,0,0.05)" }}>
        
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px"}}>
          <h3>🛡️ Warranty Information</h3>
          {/* ✅ START WARRANTY BUTTON */}
          <button 
            type="button" 
            onClick={handleStartWarranty}
            style={{ background: "#ff9800", color: "white", border: "none", padding: "8px 15px", borderRadius: "5px", cursor: "pointer", fontSize: "13px", fontWeight: "bold" }}
          >
            ⚡ Start Warranty Today
          </button>
        </div>

        <form onSubmit={handleWarrantySave} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
          
          {/* Project ID (Read Only) */}
          <div className="form-group">
            <label style={{ fontSize: "12px", fontWeight: "bold", color: "#666" }}>Project ID</label>
            <input type="text" value={project.clientRef} disabled style={{ width: "100%", padding: "10px", background: "#f0f0f0", border: "1px solid #ccc", borderRadius: "5px" }} />
          </div>

          {/* Panel Warranty */}
          <div className="form-group">
            <label style={{ fontSize: "12px", fontWeight: "bold", color: "#666" }}>Panel Warranty</label>
            <input 
              type="text" 
              placeholder="e.g. 25 Years"
              value={warranty.panelWarranty} 
              onChange={(e) => setWarranty({...warranty, panelWarranty: e.target.value})}
              style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "5px" }} 
            />
          </div>

          {/* Inverter Name */}
          <div className="form-group">
            <label style={{ fontSize: "12px", fontWeight: "bold", color: "#666" }}>Inverter Name</label>
            <input 
              type="text" 
              placeholder="e.g. SolarEdge / Growatt"
              value={warranty.inverterName} 
              onChange={(e) => setWarranty({...warranty, inverterName: e.target.value})}
              style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "5px" }} 
            />
          </div>

          {/* Inverter Warranty */}
          <div className="form-group">
            <label style={{ fontSize: "12px", fontWeight: "bold", color: "#666" }}>Inverter Warranty</label>
            <input 
              type="text" 
              placeholder="e.g. 10 Years"
              value={warranty.inverterWarranty} 
              onChange={(e) => setWarranty({...warranty, inverterWarranty: e.target.value})}
              style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "5px" }} 
            />
          </div>

          {/* Start Date */}
          <div className="form-group">
            <label style={{ fontSize: "12px", fontWeight: "bold", color: "#666" }}>Warranty Start Date</label>
            <input 
              type="date" 
              value={warranty.startDate ? warranty.startDate.split('T')[0] : ""} 
              onChange={(e) => setWarranty({...warranty, startDate: e.target.value})}
              style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "5px" }} 
            />
          </div>

          {/* End Date */}
          <div className="form-group">
            <label style={{ fontSize: "12px", fontWeight: "bold", color: "#666" }}>Warranty End Date</label>
            <input 
              type="date" 
              value={warranty.endDate ? warranty.endDate.split('T')[0] : ""} 
              onChange={(e) => setWarranty({...warranty, endDate: e.target.value})}
              style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "5px" }} 
            />
          </div>

          <div style={{ gridColumn: "span 2", textAlign: "right" }}>
            <button type="submit" style={{ background: "#28a745", color: "white", padding: "10px 20px", border: "none", borderRadius: "5px", cursor: "pointer", fontWeight: "bold" }}>
              💾 Save Warranty Details
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}